import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import Skeleton from "@mui/material/Skeleton";
import close from "../../../assets/Images/icons/Cancel.svg";
import styled from "styled-components";
import API from "../../../api/axios";
import { APP_URLS } from "../../../api/url";
import { APIError } from "../../../core/utils";
import { NavLink } from "react-router-dom";

const MetricCard = styled.div`
    background: #f8f9fa;
    border-radius: 8px;
    padding: 20px;
    margin-bottom: 20px;
    border: 1px solid #e0e0e0;
`;

const MetricTitle = styled.h5`
    font-size: 16px;
    font-weight: 600;
    margin-bottom: 15px;
    color: #333;
`;

const MetricList = styled.ul`
    list-style: none;
    padding: 0;
    margin: 10px 0 0 0;
`;

const MetricListItem = styled.li`
    display: flex;
    justify-content: space-between;
    align-items: center;
    ${({ textVariant }) => textVariant && `
        padding: 8px 4px;
        border-bottom: 1px solid #e0e0e0;
    `}
    &:last-child {
        border-bottom: none;
    }
`;

const MetricLabel = styled.span`
    font-size: 14px;
    color: #333;
`;

const MetricCount = styled.span`
    font-size: 14px;
    font-weight: 600;
    color: #637df4;
    height: 20px;
`;

const MetricListItemLink = styled(NavLink)`
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    text-decoration: none;
    color: inherit;
    cursor: pointer;
    padding: 8px 4px;
    border-bottom: 1px solid #e0e0e0;



    &:hover {
        transform: translateY(-2px);
        box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
    }
`;

const MetricListItemConatiner = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    text-decoration: none;
    color: inherit;
    /* cursor: pointer; */
    padding: 8px 4px;
    border-bottom: 1px solid #e0e0e0;



    /* &:hover {
        transform: translateY(-2px);
        box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
    } */
`;

const MetricsGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 16px;
    margin-bottom: 20px;
`;

const MetricBox = styled(NavLink)`
    background: #f8f9fa;
    border-radius: 8px;
    padding: 16px;
    border: 1px solid #e0e0e0;
    text-align: center;
    text-decoration: none;
`;

const MetricBoxContainer = styled.div`
    background: #f8f9fa;
    border-radius: 8px;
    padding: 16px;
    border: 1px solid #e0e0e0;
    text-align: center;
    text-decoration: none;
`;

const MetricBoxValue = styled.div`
    font-size: 28px;
    font-weight: 700;
    color: #637df4;
    margin-bottom: 8px;
    height: 20px;
`;

const MetricBoxCount = styled.div`
    font-size: 14px;
    font-weight: 600;
    color: #333;
    margin-bottom: 8px;
    height: 20px;
`;

const MetricBoxLabel = styled.div`
    font-size: 14px;
    color: #666;
`;

const FilterRow = styled.div`
    display: flex;
    gap: 16px;
    align-items: center;
    margin-bottom: 20px;
    flex-wrap: wrap;
`;

const FilterGroup = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`;

const FilterLabel = styled.label`
    font-size: 14px;
    color: #333;
    margin: 0;
    white-space: nowrap;
`;

const FilterSelect = styled.select`
    padding: 8px 12px;
    font-size: 14px;
    border: 1px solid #e0e0e0;
    border-radius: 6px;
    background: #fff;
    min-width: 120px;
`;

const MONTHS = [
    { value: "1", label: "January" },
    { value: "2", label: "February" },
    { value: "3", label: "March" },
    { value: "4", label: "April" },
    { value: "5", label: "May" },
    { value: "6", label: "June" },
    { value: "7", label: "July" },
    { value: "8", label: "August" },
    { value: "9", label: "September" },
    { value: "10", label: "October" },
    { value: "11", label: "November" },
    { value: "12", label: "December" },
];

const currentYear = new Date().getFullYear();
const currentMonth = String(new Date().getMonth() + 1);
const YEARS = Array.from({ length: 6 }, (_, i) => ({
    value: String(currentYear - i),
    label: String(currentYear - i),
}));

const TOAST_POSITION = toast.POSITION.TOP_RIGHT;

/** Human-readable labels for per_vertical_counts keys from the API. Always show these rows. */
const VERTICAL_LABELS = {
    buy: "Buy a car",
    finance: "Car Financing",
    insurance: "Car insurance",
    sell_direct: "Sell my car (Direct Offer)",
    sell_brokerage: "Sell my car (Brokerage)",
};
/** Vertical code to id (verticals table). Used for lead_vertical filter links. */
const VERTICAL_IDS = {
    sell_direct: 1,
    sell_brokerage: 2,
    buy: 3,
    insurance: 4,
    finance: 5,
};
const VERTICAL_NAMES = Object.values(VERTICAL_LABELS);

/** Maps API response (data.item shape) to the shape expected by the UI. */
function mapMetricsResponse(data) {
    if (!data) {
        return {
            pctNoToggle: 0,
            pctLeaveApp: 0,
            pctTogglesActivated: 0,
            countNoToggle: 0,
            countLeaveApp: 0,
            countTogglesActivated: 0,
            usersAcceptedOne: 0,
            usersAcceptedTwo: 0,
            usersAcceptedThree: 0,
            togglesPerVertical: [],
        };
    }
    const payload = data.data ?? data;
    const item = payload.item ?? payload;
    const num = (v) => (v != null && v !== "") ? Number(v) : 0;
    const perVertical = item.per_vertical_counts && typeof item.per_vertical_counts === "object"
        ? Object.entries(item.per_vertical_counts).map(([key, count]) => ({
            name: VERTICAL_LABELS[key] ?? key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()),
            count: num(count) || "0",
        }))
        : [];
    return {
        pctNoToggle: num(item.no_toggle_percentage),
        pctLeaveApp: num(item.abandonment_percentage),
        pctTogglesActivated: num(item.toggle_activation_percentage),
        countNoToggle: num(item.no_toggle_count),
        countLeaveApp: num(item.abandonment_count),
        countTogglesActivated: num(item.toggles_activated_count),
        usersAcceptedOne: num(item.users_single_vertical_count),
        usersAcceptedTwo: num(item.users_double_vertical_count),
        usersAcceptedThree: num(item.users_triple_vertical_count),
        togglesPerVertical: perVertical,
    };
}

const MetricsModal = () => {
    const [open, setOpen] = useState(false);
    const [selectedMonth, setSelectedMonth] = useState(currentMonth);
    const [selectedYear, setSelectedYear] = useState(String(currentYear));
    const [metricsData, setMetricsData] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!open) return;
        let cancelled = false;
        const params = new URLSearchParams();
        if (selectedMonth) params.set("month", selectedMonth);
        if (selectedYear) params.set("year", selectedYear);
        const query = params.toString();
        const url = query ? `${APP_URLS.LEADS_METRICS}?${query}` : APP_URLS.LEADS_METRICS;

        setLoading(true);
        API.get(url)
            .then((response) => {
                if (cancelled) return;
                const data = response.data?.data ?? response.data;
                setMetricsData(mapMetricsResponse(data != null ? { data } : null));
                setLoading(false);
            })
            .catch((error) => {
                if (cancelled) return;
                setLoading(false);
                const errorMessage = error?.response ? APIError(error) : error?.message || "Failed to fetch metrics";
                toast.error(errorMessage, { position: TOAST_POSITION });
            });

        return () => { cancelled = true; };
    }, [open, selectedMonth, selectedYear]);

    const handleClose = () => {
        setOpen(false);
    };

    return (
        <>
            <button className="btn" onClick={() => setOpen(true)}>
                Metrics
            </button>
            {open && (
                <div className="modal fade show custom-modal modal-backdrop">
                    <div className="modal">
                        <div className="modal-dialog modal-dialog-centered" role="document">
                            <div className="modal-content">
                                <div className="modal-header">
                                    <h5 className="modal-title">Lead Screen Metrics</h5>
                                    <img
                                        src={close}
                                        alt="close"
                                        className="mx-2"
                                        role="button"
                                        onClick={handleClose}
                                    />
                                </div>
                                <div className="modal-body" style={{ maxHeight: "60vh", overflowY: "auto" }}>
                                    {/* Filters */}
                                    <FilterRow>
                                        <FilterGroup>
                                            <FilterLabel htmlFor="metrics-month">Month</FilterLabel>
                                            <FilterSelect
                                                id="metrics-month"
                                                value={selectedMonth}
                                                onChange={(e) => setSelectedMonth(e.target.value)}
                                            >
                                                {MONTHS.map((m) => (
                                                    <option key={m.value} value={m.value}>
                                                        {m.label}
                                                    </option>
                                                ))}
                                            </FilterSelect>
                                        </FilterGroup>
                                        <FilterGroup>
                                            <FilterLabel htmlFor="metrics-year">Year</FilterLabel>
                                            <FilterSelect
                                                id="metrics-year"
                                                value={selectedYear}
                                                onChange={(e) => setSelectedYear(e.target.value)}
                                            >
                                                {YEARS.map((y) => (
                                                    <option key={y.value} value={y.value}>
                                                        {y.label}
                                                    </option>
                                                ))}
                                            </FilterSelect>
                                        </FilterGroup>
                                    </FilterRow>

                                    {(() => {
                                        const display = metricsData || mapMetricsResponse(null);
                                        const pct = (v) => `${v}%`;
                                        const valueSkeleton = <Skeleton variant="text" width={64} height={20} sx={{ display: "inline-block", verticalAlign: "middle" }} />;
                                        const countSkeleton = <Skeleton variant="text" width={48} height={20} sx={{ display: "inline-block", verticalAlign: "middle" }} />;
                                        return (
                                            <>
                                                <MetricsGrid>
                                                    {/* <MetricBox to={`/users?lead_consent=2`}> */}
                                                    <MetricBoxContainer>
                                                        <MetricBoxValue>{loading ? valueSkeleton : pct(display.pctNoToggle)}</MetricBoxValue>
                                                        <MetricBoxCount>{loading ? countSkeleton : `(${display.countNoToggle})`}</MetricBoxCount>
                                                        <MetricBoxLabel>Users who did not select any toggle</MetricBoxLabel>
                                                    </MetricBoxContainer>
                                                    {/* <MetricBox to={`/users?lead_consent=0`}> */}
                                                    <MetricBoxContainer>
                                                        <MetricBoxValue>{loading ? valueSkeleton : pct(display.pctLeaveApp)}</MetricBoxValue>
                                                        <MetricBoxCount>{loading ? countSkeleton : `(${display.countLeaveApp})`}</MetricBoxCount>
                                                        <MetricBoxLabel>Users who left the app with no action</MetricBoxLabel>
                                                    </MetricBoxContainer>
                                                    {/* <MetricBox to={`/users?lead_consent=1`}> */}
                                                    <MetricBoxContainer>
                                                        <MetricBoxValue>{loading ? valueSkeleton : pct(display.pctTogglesActivated)}</MetricBoxValue>
                                                        <MetricBoxCount>{loading ? countSkeleton : `(${display.countTogglesActivated})`}</MetricBoxCount>
                                                        <MetricBoxLabel>User who activated at least one toggle</MetricBoxLabel>
                                                    </MetricBoxContainer>
                                                </MetricsGrid>

                                                <MetricCard>
                                                    <MetricTitle>Acceptance by vertical count</MetricTitle>
                                                    <MetricList>
                                                        <MetricListItem textVariant="true">
                                                            <MetricLabel>Users who accepted one vertical</MetricLabel>
                                                            <MetricCount>{loading ? countSkeleton : display.usersAcceptedOne}</MetricCount>
                                                        </MetricListItem>
                                                        <MetricListItem textVariant="true">
                                                            <MetricLabel>Users who accepted two verticals</MetricLabel>
                                                            <MetricCount>{loading ? countSkeleton : display.usersAcceptedTwo}</MetricCount>
                                                        </MetricListItem>
                                                        <MetricListItem textVariant="true">
                                                            <MetricLabel>Users who accepted three or more verticals</MetricLabel>
                                                            <MetricCount>{loading ? countSkeleton : display.usersAcceptedThree}</MetricCount>
                                                        </MetricListItem>
                                                    </MetricList>
                                                </MetricCard>

                                                <MetricCard>
                                                    <MetricTitle>Toggles activated per vertical in {MONTHS.find((m) => m.value === selectedMonth)?.label} {selectedYear}</MetricTitle>
                                                    <MetricList>
                                                        {VERTICAL_NAMES.map((name) => {
                                                            const row = display.togglesPerVertical?.find((v) => v.name === name);
                                                            const count = row != null ? row.count : 0;
                                                            const code = Object.entries(VERTICAL_LABELS).find(([, label]) => label === name)?.[0];
                                                            const id = code != null ? VERTICAL_IDS[code] : null;
                                                            return (
                                                                <MetricListItem key={name}>
                                                                    {/* <MetricListItemLink to={id != null ? `/users?lead_vertical=${id}` : "#"}> */}
                                                                    <MetricListItemConatiner>
                                                                        <MetricLabel>{name}</MetricLabel>
                                                                        <MetricCount>{loading ? countSkeleton : count}</MetricCount>
                                                                    </MetricListItemConatiner>
                                                                </MetricListItem>
                                                            );
                                                        })}
                                                    </MetricList>
                                                </MetricCard>
                                            </>
                                        );
                                    })()}
                                </div>
                                <div className="d-flex justify-content-end p-3">
                                    <button
                                        type="button"
                                        className="btn cancel-btn btn-border"
                                        onClick={handleClose}
                                    >
                                        Close
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default MetricsModal;

